import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import { todayKey, formatDateLabel } from '../../shared/daily';
import { finish as recordOutcome, getResult, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import {
  CONSTANTS,
  Dictionary,
  LongshotGame as Engine,
  RANKS,
  TILE_COUNT,
  faceDisplay,
  heatLevel,
  perfectOrderTotal,
  rankFor,
  rankProgress,
  tileValue,
  type PuzzleDef,
  type Rank,
  type RankThresholds,
} from './engine';
import { fetchLongshotDay, loadLongshotDictionary, rankLadder } from './puzzle';
import './longshot.css';

/** A finished run, stored in the daily result so reopening the day can redraw it. */
interface LongshotRun {
  puzzleId: string;
  board: string;
  gold: number;
  score: number;
  /** Resolved at finish time, like iOS — a later ladder change can't rewrite it. */
  rank: Rank | null;
  thresholds: RankThresholds | null;
  shots: Array<{
    word: string;
    path: number[];
    valid: boolean;
    base: number;
    rung: number;
    points: number;
    gold: boolean;
  }>;
}

/** In-progress snapshot: the fired paths. Replaying them rebuilds the exact state. */
interface LongshotSnapshot {
  puzzleId: string;
  paths: number[][];
}

export default function LongshotGamePage() {
  // Local calendar day, the same key iOS uses (`LongshotPuzzleProvider.dayKey`).
  const [dayKey] = useState(todayKey);
  const [finishedRun] = useState(() =>
    isFinished('longshot', dayKey)
      ? ((getResult('longshot', dayKey)?.detail as LongshotRun | undefined) ?? null)
      : null,
  );
  const [data, setData] = useState<{ puzzle: PuzzleDef; dictionary: Dictionary } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (finishedRun) return;
    let cancelled = false;
    Promise.all([fetchLongshotDay(dayKey), loadLongshotDictionary()])
      .then(([puzzle, dictionary]) => {
        if (cancelled) return;
        if (!puzzle) {
          setLoadError("Today's Longshot board hasn't been published yet.");
          return;
        }
        setData({ puzzle, dictionary });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [dayKey, finishedRun]);

  // A daily is one attempt: a scored day shows its ticket, never a fresh board.
  if (finishedRun) {
    return (
      <GameShell gameId="longshot" subtitle="Today's run is complete">
        <div className="ls-page">
          <Ticket run={finishedRun} dayKey={dayKey} />
        </div>
      </GameShell>
    );
  }
  if (loadError) return <ErrorScreen message={loadError} gameId="longshot" />;
  if (!data) {
    return (
      <GameShell gameId="longshot">
        <LoadingScreen label="Loading today's board…" />
      </GameShell>
    );
  }
  return <LongshotPlay puzzle={data.puzzle} dictionary={data.dictionary} dayKey={dayKey} />;
}

function LongshotPlay({
  puzzle,
  dictionary,
  dayKey,
}: {
  puzzle: PuzzleDef;
  dictionary: Dictionary;
  dayKey: string;
}) {
  const { restored, save } = useDailySnapshot<LongshotSnapshot>('longshot', dayKey);

  // The engine is mutable and never replaced; `bump` forces the re-render.
  const [engine] = useState(() => {
    const g = new Engine(puzzle, dictionary);
    // Only resume onto the board the snapshot was taken on.
    if (restored?.puzzleId === puzzle.id) {
      for (const path of restored.paths) {
        g.clear();
        for (const t of path) g.traceTile(t);
        if (!g.canFire) break;
        g.fire();
      }
      g.clear();
    }
    return g;
  });
  const [, bump] = useState(0);
  const rerender = useCallback(() => bump((v) => v + 1), []);

  const [run, setRun] = useState<LongshotRun | null>(null);
  const [shaking, setShaking] = useState(false);
  const [sweepRung, setSweepRung] = useState<number | null>(null);
  const [celebration, setCelebration] = useState<number | null>(null);

  const shotCount = engine.shotLog.length;
  const snapshot = useMemo<LongshotSnapshot>(
    () => ({ puzzleId: puzzle.id, paths: engine.shotLog.map((s) => [...s.path]) }),
    [puzzle.id, shotCount],
  );
  // Nothing spent yet means nothing worth saving, same as iOS `saveProgress`.
  usePersistedState(save, snapshot, run === null && shotCount > 0);

  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const trace = useCallback(
    (tile: number) => {
      if (run || engine.isOver) return;
      if (engine.traceTile(tile)) rerender();
    },
    [engine, run, rerender],
  );

  const fire = () => {
    if (!engine.canFire) return;
    const shot = engine.fire();

    if (!shot.valid) {
      setShaking(true);
      later(() => setShaking(false), 320);
      // The ladder resets right-to-left, visibly undoing the climb.
      const rungs = Array.from({ length: CONSTANTS.MULT_MAX }, (_, i) => CONSTANTS.MULT_MAX - i);
      rungs.forEach((rung, step) => later(() => setSweepRung(rung), step * 50));
      later(() => setSweepRung(null), rungs.length * 50 + 120);
    } else if (shot.rungAfter >= 3 && shot.rungAfter > shot.rung) {
      setCelebration(shot.rungAfter);
      later(() => setCelebration(null), 900);
    }

    if (engine.isOver) {
      const finished = toRun(engine, puzzle);
      const hits = finished.shots.filter((s) => s.valid).length;
      recordOutcome('longshot', dayKey, 'won', {
        subtitle: `Scored ${finished.score} with ${hits} ${hits === 1 ? 'hit' : 'hits'}.`,
        detail: finished,
      });
      setRun(finished);
    }
    rerender();
  };

  const clear = () => {
    engine.clear();
    rerender();
  };

  if (run) {
    return (
      <GameShell gameId="longshot" subtitle="Today's run is complete">
        <div className="ls-page">
          <Ticket run={run} dayKey={dayKey} />
        </div>
      </GameShell>
    );
  }

  const preview = engine.preview();
  const word = engine.currentWord;
  const ladder = rankLadder(puzzle);

  return (
    <GameShell
      gameId="longshot"
      subtitle={`${formatDateLabel(dayKey, true)} · ${puzzle.analytics.difficulty.toUpperCase()}`}
    >
      <div className="ls-page">
        <section className="ls-block ls-status">
          <Stat label="SCORE" value={String(engine.score)} />
          <div className="ls-bullets-wrap">
            <div className="ls-label">BULLETS</div>
            <div className="ls-bullets" aria-label={`${engine.bullets} bullets left`}>
              {Array.from({ length: CONSTANTS.BULLETS }, (_, i) => (
                <span key={i} className="ls-bullet" data-live={i < engine.bullets} />
              ))}
            </div>
          </div>
          <RankStat score={engine.score} thresholds={ladder} />
        </section>

        <Ladder rung={engine.rung} sweepRung={sweepRung} />

        <div className="ls-board-wrap">
          <Board
            puzzle={puzzle}
            trace={engine.currentPath as number[]}
            uses={usesBefore(engine.shotLog, engine.shotLog.length)}
            shaking={shaking}
            onTrace={trace}
          />
          {celebration !== null && <div className="ls-celebration">×{celebration}</div>}
        </div>

        <section className="ls-block ls-tracer">
          <span className="ls-tracer-word" data-empty={word.length === 0}>
            {word || 'TRACE A WORD'}
          </span>
          {word.length > 0 && (
            <span className="ls-tracer-score">
              <span className="ls-tracer-points">{preview.points}</span>
              {/* The SUM, not the length: it's the number routing through hot
                  tiles builds, and watching it climb teaches the mechanic. */}
              <span className="ls-tracer-breakdown">
                {preview.sum}²{preview.gold ? ` ×${CONSTANTS.GOLD_MULT} gold` : ''} ×{engine.rung}
              </span>
            </span>
          )}
        </section>

        <div className="ls-controls">
          {/* Identical for a word that will land and one that won't: validity is
              only learned by firing. A hint here would delete the game. */}
          <button className="btn btn-primary ls-fire" onClick={fire} disabled={!engine.canFire}>
            {fireLabel(engine)}
          </button>
          <button className="btn btn-secondary" onClick={clear} disabled={word.length === 0}>
            CLEAR
          </button>
        </div>

        <p className="ls-help">
          Drag across neighbouring tiles to spell a word of {CONSTANTS.MIN_LEN}+ letters, then fire.
          Hits climb the ladder; a miss resets it to ×1 and still costs the bullet.{' '}
          <strong>Every tile you reuse gains +{CONSTANTS.HEAT_STEP}</strong>, and a word scores the{' '}
          <em>square</em> of its tiles' values, so route late words back over your own tracks. The
          purple tile doubles the word.
        </p>
      </div>
    </GameShell>
  );
}

// ---------------------------------------------------------------- helpers

/** Per-tile use counts as they stood before shot `k` fired. */
function usesBefore(shots: ReadonlyArray<{ path: readonly number[]; valid: boolean }>, k: number) {
  const uses = new Array<number>(TILE_COUNT).fill(0);
  for (let i = 0; i < k; i++) {
    if (!shots[i].valid) continue;
    for (const t of shots[i].path) uses[t] += 1;
  }
  return uses;
}

function toRun(engine: Engine, puzzle: PuzzleDef): LongshotRun {
  const thresholds = rankLadder(puzzle);
  return {
    puzzleId: puzzle.id,
    board: puzzle.board,
    gold: puzzle.gold,
    score: engine.score,
    rank: thresholds ? rankFor(engine.score, thresholds) : null,
    thresholds,
    shots: engine.shotLog.map((s) => ({
      word: s.word,
      path: [...s.path],
      valid: s.valid,
      base: s.base,
      rung: s.rung,
      points: s.points,
      gold: s.gold,
    })),
  };
}

function fireLabel(engine: Engine): string {
  switch (engine.fireRejection()) {
    case 'too-short':
      return engine.currentWord.length === 0 ? 'FIRE' : `NEED ${CONSTANTS.MIN_LEN} LETTERS`;
    case 'too-long':
      return 'TOO LONG';
    case 'already-fired':
      return 'ALREADY FIRED';
    case 'no-bullets':
      return 'OUT OF BULLETS';
    default:
      return `FIRE AT ×${engine.rung}`;
  }
}

const RANK_LABEL: Record<Rank, string> = {
  bronze: 'BRONZE',
  silver: 'SILVER',
  gold: 'GOLD',
  platinum: 'PLATINUM',
  expert: 'EXPERT',
};

const RANK_BLURB: Record<Rank, string> = {
  bronze: 'You got on the board.',
  silver: 'A solid, sensible run.',
  gold: 'Well above an ordinary day.',
  platinum: 'You are routing heat properly.',
  expert: 'Past what normal vocabulary reaches.',
};

/** Shows the next threshold, never the solver ceiling — same framing as iOS. */
function nextRankLabel(score: number, thresholds: RankThresholds): string {
  const { next } = rankProgress(score, thresholds);
  if (!next) return 'MAXED';
  const target = thresholds[RANKS.indexOf(next)];
  return `${target - score} more for ${RANK_LABEL[next]}`;
}

// ---------------------------------------------------------------- pieces

function Stat({ label, value, alignRight }: { label: string; value: string; alignRight?: boolean }) {
  return (
    <div style={alignRight ? { textAlign: 'right' } : undefined}>
      <div className="ls-label">{label}</div>
      <div className="ls-stat-value">{value}</div>
    </div>
  );
}

/** Points still needed for the next tier — the reachable target, not the ceiling. */
function RankStat({ score, thresholds }: { score: number; thresholds: RankThresholds | null }) {
  if (!thresholds) return <Stat label="RANK" value="—" alignRight />;
  const { next } = rankProgress(score, thresholds);
  if (!next) return <Stat label="RANK" value={RANK_LABEL.expert} alignRight />;
  return (
    <Stat
      label={`TO ${RANK_LABEL[next]}`}
      value={String(thresholds[RANKS.indexOf(next)] - score)}
      alignRight
    />
  );
}

function Ladder({ rung, sweepRung }: { rung: number; sweepRung: number | null }) {
  return (
    <section className="ls-ladder" aria-label={`Multiplier ×${rung}`}>
      {Array.from({ length: CONSTANTS.MULT_MAX }, (_, i) => {
        const level = i + 1;
        const state = level === rung ? 'active' : level < rung ? 'climbed' : 'idle';
        return (
          <div key={level} className="ls-rung" data-state={state} data-sweep={sweepRung === level}>
            ×{level}
            {state === 'active' && <small>NOW</small>}
          </div>
        );
      })}
    </section>
  );
}

function Board({
  puzzle,
  trace,
  uses,
  shaking,
  onTrace,
}: {
  puzzle: PuzzleDef;
  trace: number[];
  uses: number[];
  shaking: boolean;
  onTrace: (tile: number) => void;
}) {
  const dragging = useRef(false);
  const lastTile = useRef<number | null>(null);

  // Hit-test by point rather than pointerenter: a touch drag keeps firing events
  // at the element it started on, so elementFromPoint is what finds the tile
  // under the finger.
  const tileAt = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-tile]');
    return el ? Number(el.dataset.tile) : null;
  };

  const touch = (tile: number | null) => {
    if (tile === null || tile === lastTile.current) return;
    lastTile.current = tile;
    onTrace(tile);
  };

  useEffect(() => {
    const up = () => {
      dragging.current = false;
      lastTile.current = null;
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, []);

  const order = new Map<number, number>();
  trace.forEach((tile, i) => order.set(tile, i + 1));

  return (
    <section
      className="ls-board"
      data-shake={shaking}
      onPointerDown={(e) => {
        dragging.current = true;
        lastTile.current = null;
        touch(tileAt(e.clientX, e.clientY));
      }}
      onPointerMove={(e) => {
        if (dragging.current) touch(tileAt(e.clientX, e.clientY));
      }}
    >
      {Array.from({ length: TILE_COUNT }, (_, index) => {
        const level = heatLevel(uses[index]);
        const isGold = index === puzzle.gold;
        const position = order.get(index);
        const value = tileValue(puzzle.board, index, uses[index]);
        return (
          <div
            key={index}
            data-tile={index}
            className="ls-tile"
            data-traced={position !== undefined}
            data-gold={isGold}
            data-heat={Math.min(level, 2)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTrace(index);
              }
            }}
            aria-label={[
              faceDisplay(puzzle.board[index]),
              position !== undefined ? `position ${position}` : '',
              isGold ? 'doubles the word' : '',
              level > 0 ? `worth ${value}` : '',
            ]
              .filter(Boolean)
              .join(', ')}
          >
            {faceDisplay(puzzle.board[index])}
            {/* Badge slots are fixed: order top-left, ×2 top-right, value bottom-left. */}
            {position !== undefined && <span className="ls-badge ls-badge-order">{position}</span>}
            {isGold && <span className="ls-badge ls-badge-gold">×{CONSTANTS.GOLD_MULT}</span>}
            {level > 0 && (
              <span className="ls-badge ls-badge-heat" data-level={Math.min(level, 2)}>
                {value}
              </span>
            )}
          </div>
        );
      })}
    </section>
  );
}

function Ticket({ run, dayKey }: { run: LongshotRun; dayKey: string }) {
  const [copied, setCopied] = useState(false);
  const validBases = run.shots.filter((s) => s.valid).map((s) => s.base);
  const hits = validBases.length;
  const perfectTotal = perfectOrderTotal(validBases);
  const delta = Math.max(0, perfectTotal - run.score);

  const share = async () => {
    const marks = run.shots.map((s) => (s.valid ? '▮' : '✗')).join('');
    const rank = run.rank ? ` · ${RANK_LABEL[run.rank]}` : '';
    const text = `LONGSHOT ${run.puzzleId} — ${run.score}${rank}\n${marks}\n${window.location.origin}${window.location.pathname}#/play/longshot`;
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch {
        // Dismissed — fall through to the clipboard.
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked; nothing else to try.
    }
  };

  return (
    <>
      <section className="ls-block ls-ticket">
        <div className="ls-ticket-head">
          <div className="ls-label">LONGSHOT · {run.puzzleId}</div>
          <div className="ls-ticket-score">{run.score}</div>
          <div className="ls-ticket-marks">
            {run.shots.map((s, i) => (
              <span key={i} className={s.valid ? 'ls-mark-hit' : 'ls-mark-miss'}>
                {s.valid ? '▮' : '✗'}
              </span>
            ))}
          </div>
        </div>

        {run.thresholds && <RankBadge score={run.score} rank={run.rank} thresholds={run.thresholds} />}

        <div className="ls-perf" />

        <div className="ls-shots">
          {run.shots.map((shot, k) => {
            const uses = usesBefore(run.shots, k);
            const values = shot.path.map((t) => tileValue(run.board, t, uses[t]));
            const sum = values.reduce((a, b) => a + b, 0);
            return (
              <div key={k} className="ls-shot" data-valid={shot.valid}>
                <div className="ls-word-tiles">
                  {shot.path.map((t, i) => (
                    <span
                      key={i}
                      className="ls-mini-tile"
                      data-gold={t === run.gold}
                      data-heat={Math.min(heatLevel(uses[t]), 2)}
                    >
                      {faceDisplay(run.board[t])}
                      <span className="ls-mini-value">{values[i]}</span>
                    </span>
                  ))}
                </div>
                {shot.valid ? (
                  <div className="ls-shot-line">
                    <span className="ls-shot-math">
                      {values.join('+')} = {sum}²{shot.gold ? ` ×${CONSTANTS.GOLD_MULT} gold` : ''} ×
                      {shot.rung}
                    </span>
                    <span className="ls-shot-points">{shot.points}</span>
                  </div>
                ) : (
                  <div className="ls-shot-line">
                    <span className="ls-shot-math ls-mark-miss">MISS — bullet spent, ladder reset</span>
                    <span className="ls-shot-points">0</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hits > 1 && (
          <>
            <div className="ls-perf" />
            {/* A reordering of the shots actually fired, NOT the best possible
                game. The board's best word stays hidden, as on iOS. */}
            <div className="ls-stat-row">
              <div className="ls-label">SAME SHOTS, PERFECT ORDER</div>
              <div>
                <span className="ls-value">{perfectTotal}</span>{' '}
                <span className="ls-note">
                  {delta > 0 ? `you left ${delta} on the table` : 'you fired them in the ideal order'}
                </span>
              </div>
            </div>
          </>
        )}
      </section>

      <div className="ls-controls">
        <button className="btn btn-primary ls-fire" onClick={share}>
          {copied ? 'COPIED ✓' : 'SHARE'}
        </button>
        <Link to="/" className="btn btn-secondary">
          DONE
        </Link>
      </div>
      <p className="ls-help">
        {formatDateLabel(dayKey, true)} · One board a day — come back tomorrow for a fresh one.
      </p>
    </>
  );
}

function RankBadge({
  score,
  rank,
  thresholds,
}: {
  score: number;
  rank: Rank | null;
  thresholds: RankThresholds;
}) {
  const { next, fraction } = rankProgress(score, thresholds);
  return (
    <div className="ls-rank-wrap">
      <div className="ls-rank" data-rank={rank ?? 'none'}>
        <div className="ls-rank-title">{rank ? RANK_LABEL[rank] : 'UNRANKED'}</div>
        <div className="ls-rank-blurb">{rank ? RANK_BLURB[rank] : 'Score a little more to reach Bronze.'}</div>
      </div>
      {next && (
        <div className="ls-rank-next">
          <div className="ls-rank-bar">
            <span style={{ width: `${Math.round(fraction * 100)}%` }} />
          </div>
          <span className="ls-note">{nextRankLabel(score, thresholds)}</span>
        </div>
      )}
    </div>
  );
}
